import { HatElement } from '@/components/hat-element.js';
import { assetsClient } from '@/clients/assets-client.js';
import { assetTypesCache } from '@/services/asset-types-cache.js';
import type { MaintenanceTemplateGroup } from '@/clients/assets-client.js';

const FREQ_LABELS: Record<string, string> = { D: 'day', W: 'week', M: 'month', Y: 'year' };

function formatFreq(value: number, unit: string): string {
    const label = FREQ_LABELS[unit] ?? unit;
    return `Every ${value} ${value === 1 ? label : label + 's'}`;
}

class HatAdminMaintenanceTemplates extends HatElement {
    private _groups: MaintenanceTemplateGroup[] = [];
    private _labelMap = new Map<string, string>();

    connectedCallback() {
        super.connectedCallback();
        this._renderShell();
        this._load();
    }

    async _load() {
        try {
            const [groups, assetTypes] = await Promise.all([
                assetsClient.listMaintenanceTemplates(),
                assetTypesCache.getAll(),
            ]);
            this._groups = groups;
            this._labelMap = new Map(assetTypes.map(t => [t.code ?? '', t.description ?? t.code ?? '']));
        } catch {
            this._groups = [];
        }
        this._renderContent();
    }

    _renderShell() {
        this.root.innerHTML = `
            <style>
                h2 {
                    color: white;
                    font-size: 1.4rem;
                    margin: 0 0 20px;
                    text-shadow: 0 2px 4px rgba(0,0,0,0.3);
                }

                .card {
                    background: white;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                    table-layout: fixed;
                }

                col.col-item { width: 70%; }
                col.col-freq  { width: 30%; }

                thead th {
                    text-align: left;
                    padding: 10px 16px;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    color: #999;
                    background: #f8f8f8;
                    border-bottom: 2px solid #eee;
                }

                tr.group-row td {
                    background: #f0f0f0;
                    padding: 7px 16px;
                    font-size: 0.78rem;
                    font-weight: 700;
                    font-family: monospace;
                    color: #444;
                    letter-spacing: 0.04em;
                    border-top: 1px solid #ddd;
                }

                tr.group-row:first-of-type td {
                    border-top: none;
                }

                td {
                    padding: 9px 16px;
                    border-bottom: 1px solid #f5f5f5;
                    font-size: 0.88rem;
                    vertical-align: middle;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                tr:last-child td { border-bottom: none; }

                .item-label { font-weight: 500; }

                .item-code {
                    font-family: monospace;
                    font-size: 0.82rem;
                    color: #888;
                    font-weight: 400;
                }

                .freq { color: #666; }

                .loading {
                    text-align: center;
                    padding: 40px;
                    color: rgba(255,255,255,0.7);
                }
            </style>

            <h2>Maintenance Templates</h2>
            <div id="content"><p class="loading">Loading…</p></div>
        `;
    }

    _renderContent() {
        const content = this.root.getElementById('content');
        if (!content) return;

        if (!this._groups.length) {
            content.innerHTML = `<p class="loading">No templates found.</p>`;
            return;
        }

        const rows = this._groups.map(g => {
            const label = this._labelMap.get(g.assetType) ?? g.assetType;
            return `
            <tr class="group-row">
                <td colspan="2">${label} <span style="font-weight:400;color:#888;">(${g.items.length} item${g.items.length !== 1 ? 's' : ''})</span></td>
            </tr>
            ${g.items.map(item => `
                <tr>
                    <td><span class="item-label">${item.label}</span> <span class="item-code">(${item.code})</span></td>
                    <td class="freq">${formatFreq(item.frequencyValue, item.frequencyUnit)}</td>
                </tr>
            `).join('')}
        `;
        }).join('');

        content.innerHTML = `
            <div class="card">
                <table>
                    <colgroup>
                        <col class="col-item">
                        <col class="col-freq">
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Maintenance Item</th>
                            <th>Frequency</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>
        `;
    }
}

customElements.define('hat-admin-maintenance-templates', HatAdminMaintenanceTemplates);